import React, { useState, useEffect } from "react";

import { useAuth } from '../context/AuthContext';
import { useAlertContext } from '../context/AlertContext';
import { getAllMentors, requestMentorship } from '../services/mentorService';
import { FaUserTie, FaEnvelope, FaPhone, FaLinkedin, FaBriefcase, FaGraduationCap, FaChalkboardTeacher, FaUserPlus, FaUserGraduate, FaStar, FaTimes, FaCode } from 'react-icons/fa';
import { HiAcademicCap, HiSparkles, HiLightningBolt } from 'react-icons/hi';
import { IoCloseCircle } from 'react-icons/io5';

const Mentors = () => {
  const [mentors, setMentors] = useState([]);
  const [selectedMentor, setSelectedMentor] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [messageForm, setMessageForm] = useState({
    name: '',
    education: '',
    email: '',
    contact: '',
    meetingTime: '',
    meetingDay: '',
    topic: '',
    message: ''
  });
  const { user, setUser } = useAuth();
  const { showAlert } = useAlertContext();

  useEffect(() => {
    fetchMentors();
  }, []);

  const fetchMentors = async () => {
    try {
      setPageLoading(true);
      const data = await getAllMentors();
      if (data.success) {
        setMentors(data.mentors);
      }
    } catch (error) {
      console.error('Error fetching mentors:', error);
      showAlert({
        type: 'error',
        title: 'Error',
        message: 'Failed to load mentors. Please try again later.'
      });
    } finally {
      setPageLoading(false);
    }
  };

  const handleOpenCard = (mentor) => {
    setSelectedMentor(mentor);
  };

  const handleCloseCard = () => {
    setSelectedMentor(null);
  };

  const handleOpenMessageModal = () => {
    setMessageModalOpen(true);
  };

  const handleCloseMessageModal = () => {
    setMessageModalOpen(false);
    setMessageForm({
      name: '',
      education: '',
      meetingTime: '',
      meetingDay: '',
      topic: '',
      email: '',
      contact: '',
      message: ''
    });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setMessageForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    setIsSendingMessage(true);
    try {
      const requestData = {
        mentorId: selectedMentor._id,
        ...messageForm
      };

      const data = await requestMentorship(requestData);

      if (data.success) {
        showAlert({
          type: 'success',
          title: 'Request Sent',
          message: 'Your meeting request has been sent! You can track status in your dashboard.'
        });
        handleCloseMessageModal();
      } else {
        throw new Error('Failed to send request');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      showAlert({
        type: 'error',
        title: 'Error',
        message: 'Failed to send request. Please try again later.'
      });
    } finally {
      setIsSendingMessage(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f3f4f6]" style={{
        backgroundImage: `
          radial-gradient(at 0% 0%, rgba(59, 130, 246, 0.15) 0px, transparent 50%),
          radial-gradient(at 100% 0%, rgba(139, 92, 246, 0.15) 0px, transparent 50%),
          radial-gradient(at 100% 100%, rgba(236, 72, 153, 0.15) 0px, transparent 50%),
          radial-gradient(at 0% 100%, rgba(34, 197, 94, 0.15) 0px, transparent 50%)
        `
      }}>
       {/* Top Decoration */}
       <div className="h-[40rem] bg-gradient-to-r from-primary-600 to-indigo-700 w-full absolute top-0 z-0 rounded-b-[3rem] shadow-lg">
        <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
      </div>
      <div className="container-responsive relative z-10 pt-20 px-4 sm:px-6 lg:px-8 pb-16">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-md border border-white/30 text-white rounded-full text-sm font-medium mb-6 shadow-lg">
            <FaChalkboardTeacher className="w-4 h-4 mr-2" />
            <span className="tracking-wide uppercase">Expert Mentorship</span>
          </div>
          <h1 className="heading-primary mb-6 text-white drop-shadow-lg">
            Find Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-pink-200">Perfect Mentor</span>
          </h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto leading-relaxed font-light">
            Connect with industry experts who have walked the path you're on. Get personalized guidance to accelerate your career.
          </p>
        </div>

        {/* Mentors Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-8">
            {mentors.filter(mentor => !user || String(mentor.user) !== String(user._id || user.id)).map((mentor, index) => (
              <div
                key={mentor._id}
                className="group relative bg-white/80 backdrop-blur-xl rounded-3xl p-6 shadow-xl border border-white/60 hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 flex flex-col items-center text-center overflow-hidden"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Decorative Pattern inside card */}
                <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-indigo-50/50 to-transparent pointer-events-none"></div>

                {/* Expertise Chip - Premium */}
                <div className="absolute top-4 right-4 z-10">
                   <div className="px-3 py-1 bg-white/90 backdrop-blur-md border border-white/50 rounded-full shadow-lg shadow-indigo-500/10 flex items-center gap-1.5 transform hover:scale-105 transition-transform duration-300">
                      <FaCode className="text-indigo-600 text-xs" />
                      <span className="text-xs font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-violet-600">
                        {mentor.expertise ? mentor.expertise.split(',')[0].trim() : 'Expert'}
                      </span>
                   </div>
                </div>

                  {/* Avatar Section */}
                  <div className="relative mb-5 mt-2">
                    <div className="w-24 h-24 rounded-full p-1 bg-white ring-4 ring-indigo-50 shadow-lg">
                      <div className="w-full h-full rounded-full overflow-hidden bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
                        {mentor.avatar && mentor.avatar.startsWith('http') ? (
                          <img src={mentor.avatar} alt={mentor.name} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-3xl font-bold text-indigo-600">{mentor.avatar}</span>
                        )}
                      </div>
                    </div>
                     <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1 shadow-sm">
                         <div className="w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
                     </div>
                  </div>

                  {/* Info Section */}
                  <h3 className="text-xl font-bold text-gray-900 my-2 group-hover:text-indigo-600 transition-colors">
                    {mentor.name}
                  </h3>
                  
                  {/* Rating */}
                  <div className="flex items-center gap-1 mb-4 bg-yellow-50 px-3 py-1 rounded-full border border-yellow-100 shadow-sm">
                     <FaStar className="w-3.5 h-3.5 text-yellow-500" />
                     <span className="text-sm font-bold text-yellow-700">{mentor.rating}</span>
                  </div>

                  <p className="text-sm text-gray-600 mb-4 line-clamp-2 h-[2.5rem] leading-relaxed w-full px-2">{mentor.bio}</p>

                  <div className="w-full border-t border-gray-100 my-4"></div>

                  {/* Stats Pills */}
                  <div className="flex flex-wrap justify-center gap-2 w-full mb-6">
                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-50/50 border border-indigo-100 text-xs font-semibold text-indigo-700">
                      <FaBriefcase className="text-indigo-400" />
                      <span>{mentor.experience} Exp</span>
                    </div>
                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-pink-50/50 border border-pink-100 text-xs font-semibold text-pink-700">
                      <FaUserGraduate className="text-pink-400" />
                      <span>{mentor.students} Mentored</span>
                    </div>

                  </div>

                  {/* Action Button */}
                  <div className="mt-auto w-full">
                    <button
                      onClick={() => handleOpenCard(mentor)}
                      className="w-full py-3 px-4 bg-gradient-to-r from-primary-600 to-indigo-600 text-white text-sm font-bold rounded-xl shadow-md hover:shadow-lg hover:from-indigo-700 hover:to-purple-700 transform hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-2"
                    >
                      <FaUserPlus /> Connect Now
                    </button>
                  </div>
              </div>
            ))}
        </div>

        {/* Benefits Section - Glass Cards */}
        <div className="grid md:grid-cols-3 gap-6 mt-20 max-w-6xl mx-auto">
             <div className="bg-white/60 backdrop-blur-md rounded-2xl p-8 text-center border border-white/50 shadow-sm hover:shadow-md transition-all">
                <div className="w-14 h-14 bg-blue-100 text-blue-600 rounded-2xl flex items-center justify-center text-xl mx-auto mb-4 shadowed-icon">
                   <HiAcademicCap className="w-8 h-8" />
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">Expert Knowledge</h4>
                <p className="text-gray-600 text-sm">Gain insights from seasoned professionals who have mastered their craft.</p>
             </div>
             <div className="bg-white/60 backdrop-blur-md rounded-2xl p-8 text-center border border-white/50 shadow-sm hover:shadow-md transition-all">
                <div className="w-14 h-14 bg-purple-100 text-purple-600 rounded-2xl flex items-center justify-center text-xl mx-auto mb-4 shadowed-icon">
                    <HiLightningBolt className="w-8 h-8" />
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">Personalized Guidance</h4>
                <p className="text-gray-600 text-sm">Tailored advice and strategies to navigate your specific career challenges.</p>
             </div>
             <div className="bg-white/60 backdrop-blur-md rounded-2xl p-8 text-center border border-white/50 shadow-sm hover:shadow-md transition-all">
                <div className="w-14 h-14 bg-pink-100 text-pink-600 rounded-2xl flex items-center justify-center text-xl mx-auto mb-4 shadowed-icon">
                    <HiSparkles className="w-8 h-8" />
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">Career Growth</h4>
                <p className="text-gray-600 text-sm">Accelerate your professional development and reach new heights faster.</p>
             </div>
        </div>
      </div>

      {/* Mentor Info Modal - Glassmorphism */}
      {selectedMentor && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleCloseCard}></div>
          <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto relative z-10 animate-in zoom-in-95 duration-200 border border-white/50">
             
             {/* Modal Header Decoration */}
             <div className="h-32 bg-gradient-to-r from-indigo-500 to-purple-600 w-full relative">
                <button
                  className="absolute top-4 right-4 text-white/80 hover:text-white bg-black/20 hover:bg-black/30 rounded-full p-2 transition-all"
                  onClick={handleCloseCard}
                >
                  <FaTimes className="w-5 h-5" />
                </button>
             </div>

             <div className="px-8 pb-8 -mt-16 relative z-10">
                <div className="flex flex-col sm:flex-row items-start sm:items-end gap-6 mb-8">
                     <div className="w-32 h-32 rounded-3xl p-1 bg-white shadow-xl ring-1 ring-gray-100">
                        <img 
                          src={selectedMentor.avatar && selectedMentor.avatar.startsWith('http') ? selectedMentor.avatar : `https://ui-avatars.com/api/?name=${selectedMentor.name}&background=random`} 
                          alt={selectedMentor.name} 
                          className="w-full h-full object-cover rounded-2xl" 
                        />
                     </div>
                     <div className="mb-2">
                         <h2 className="heading-secondary text-white mb-10 p-2">{selectedMentor.name}</h2>
                         <div className="flex items-center space-x-3 text-gray-600">
                              <span className="bg-yellow-100 text-yellow-800 text-xs font-bold px-2.5 py-0.5 rounded flex items-center">
                                <FaStar className="w-3 h-3 mr-1" />{selectedMentor.rating}
                              </span>
                              <span className="text-sm">• {selectedMentor.experience} Experience</span>
                         </div>
                     </div>
                </div>

                <div className="space-y-8">
                    <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                            <FaUserTie className="mr-2 text-indigo-500" /> Expertise
                        </h3>
                        <div className="flex flex-wrap gap-2">
                             {selectedMentor.expertise.split(',').map((skill, index) => (
                                <span key={index} className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded-lg text-sm font-medium border border-indigo-100">
                                    {skill.trim()}
                                </span>
                             ))}
                        </div>
                    </div>

                    <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-3">About</h3>
                        <p className="text-gray-600 leading-relaxed whitespace-pre-line bg-gray-50/50 p-4 rounded-xl border border-gray-100">
                            {selectedMentor.bio}
                        </p>
                    </div>

                   <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                             <div className="text-xs text-gray-500 uppercase font-semibold mb-1">Contact Email</div>
                             <div className="font-medium text-gray-900 break-all">{selectedMentor.email}</div>
                        </div>
                         <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                             <div className="text-xs text-gray-500 uppercase font-semibold mb-1">Mobile Number</div>
                             <div className="font-medium text-gray-900">{selectedMentor.mobile ? selectedMentor.mobile.slice(0, -3) + 'xxx' : ''}</div>
                        </div>
                   </div>

                   <div className="flex gap-4 pt-4">
                        <button
                          onClick={handleOpenMessageModal}
                          className="flex-1 py-3 bg-indigo-600 text-white rounded-xl font-bold shadow-lg shadow-indigo-200 hover:bg-indigo-700 hover:shadow-xl hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2"
                        >
                            <FaEnvelope /> Send Request
                        </button>
                        <a 
                           href={selectedMentor.linkedin} 
                           target="_blank" 
                           rel="noopener noreferrer"
                           className="py-3 px-6 bg-blue-50 text-blue-700 border border-blue-100 rounded-xl font-bold hover:bg-blue-100 transition-all flex items-center justify-center gap-2"
                        >
                            <FaLinkedin /> LinkedIn
                        </a>
                   </div>
                </div>
             </div>
          </div>
        </div>
      )}

      {/* Message Modal - Glassmorphism */}
      {messageModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-[60] p-4">
           <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleCloseMessageModal}></div>
           <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto relative z-10 animate-in zoom-in-95 duration-200">
              <div className="p-8">
                  <div className="flex justify-between items-center mb-6">
                      <h2 className="heading-tertiary text-gray-900">Request Meeting</h2>
                      <button onClick={handleCloseMessageModal} className="text-gray-400 hover:text-gray-600 transition-colors">
                          <IoCloseCircle className="w-8 h-8" />
                      </button>
                  </div>
                  
                  <p className="text-gray-500 mb-6 text-sm">
                      Send a request to <span className="font-bold text-gray-800">{selectedMentor?.name}</span>. They will review your profile and get back to you.
                  </p>

                  <form onSubmit={handleSendMessage} className="space-y-5">
                       {/* Form Fields */}
                       <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                              <div>
                                  <label className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-1 block">Name</label>
                                  <input type="text" name="name" required value={messageForm.name} onChange={handleFormChange} className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all" placeholder="Your Name" />
                              </div>
                              <div>
                                  <label className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-1 block">Role</label>
                                  <input type="text" name="education" required value={messageForm.education} onChange={handleFormChange} className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all" placeholder="Student / Dev" />
                              </div>
                          </div>
                            
                          <div className="grid grid-cols-2 gap-4">
                              <div>
                                  <label className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-1 block">Email</label>
                                  <input type="email" name="email" required value={messageForm.email} onChange={handleFormChange} className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all" placeholder="you@example.com" />
                              </div>
                              <div>
                                  <label className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-1 block">Phone</label>
                                  <input type="tel" name="contact" required value={messageForm.contact} onChange={handleFormChange} className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all" placeholder="+91 999..." />
                              </div>
                          </div>

                          <div>
                              <label className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-1 block">Topic</label>
                              <input type="text" name="topic" required value={messageForm.topic} onChange={handleFormChange} className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all" placeholder="e.g. Mock Interview" />
                          </div>
                           
                          <div className="grid grid-cols-2 gap-4">
                              <div>
                                  <label className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-1 block">Pref. Day</label>
                                  <select name="meetingDay" required value={messageForm.meetingDay} onChange={handleFormChange} className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all">
                                      <option value="">Select Day</option>
                                      {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(d => <option key={d} value={d}>{d}</option>)}
                                  </select>
                              </div>
                              <div>
                                  <label className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-1 block">Pref. Time</label>
                                  <select name="meetingTime" required value={messageForm.meetingTime} onChange={handleFormChange} className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all">
                                      <option value="">Select Time</option>
                                      {['9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM'].map(t => <option key={t} value={t}>{t}</option>)}
                                  </select>
                              </div>
                          </div>

                          <div>
                              <label className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-1 block">Message</label>
                              <textarea name="message" required value={messageForm.message} onChange={handleFormChange} rows={3} className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all resize-none" placeholder="Briefly describe what you want to discuss..."></textarea>
                          </div>
                       </div>

                       <div className="pt-2">
                          <button
                            type="submit"
                            disabled={isSendingMessage}
                            className={`w-full py-4 text-white font-bold rounded-xl shadow-lg transition-all flex items-center justify-center gap-2 ${
                                isSendingMessage ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:shadow-xl hover:-translate-y-1'
                            }`}
                          >
                             {isSendingMessage ? 'Sending Request...' : 'Send Meeting Request'}
                          </button>
                       </div>
                  </form>
              </div>
           </div>
        </div>
      )}

    </div>
  );
};

export default Mentors;